import { useState, useRef, useCallback } from "react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { useDocumentAnalysis } from "shared/hooks/useDocumentAnalysis";
import type { ClassificationResult, DropzoneFile } from "../types";

interface UseFileClassificationReturn {
  classifyingFileIds: Set<string>;
  classificationResults: Record<string, ClassificationResult>;
  setClassificationResults: React.Dispatch<React.SetStateAction<Record<string, ClassificationResult>>>;
  classificationProgress: Record<string, number>;
  reanalyzingFileId: string | null;
  aiProvider: 'claude' | 'google_gpt';
  setAiProvider: React.Dispatch<React.SetStateAction<'claude' | 'google_gpt'>>;
  showBulkProgress: boolean;
  setShowBulkProgress: React.Dispatch<React.SetStateAction<boolean>>;
  isBulkAnalysisPolling: boolean;
  isStartingBackgroundProcessing: boolean;
  currentBulkJob: any;
  handleClassifyFile: (file: DropzoneFile, effectiveUnitId: string | null) => Promise<void>;
  handleReanalyzeFile: (file: DropzoneFile, effectiveUnitId: string | null) => Promise<void>;
  clearClassification: (fileId: string) => Promise<void>;
  handleStartBulkAnalysis: () => Promise<void>;
  handleBulkAnalysisCancel: () => Promise<void>;
  handleBulkAnalysisClose: () => void;
  handleBulkAnalysisRefresh: () => Promise<void>;
  handleStartBackgroundDDProcessing: () => Promise<void>;
  mergeCachedResults: (results: Record<string, ClassificationResult>) => void;
}

export function useFileClassification(
  buildingId: string,
  session: any,
  user: any,
  onBulkAnalysisClose?: () => void,
): UseFileClassificationReturn {
  const {
    startBulkAnalysisForBuilding,
    getBulkJobStatus,
    cancelBulkJob,
    currentBulkJob,
  } = useDocumentAnalysis();

  const [classifyingFileIds, setClassifyingFileIds] = useState<Set<string>>(new Set());
  const [classificationResults, setClassificationResults] = useState<Record<string, ClassificationResult>>({});
  const [classificationProgress, setClassificationProgress] = useState<Record<string, number>>({});
  const progressIntervalsRef = useRef<Record<string, NodeJS.Timeout>>({});
  const [reanalyzingFileId, setReanalyzingFileId] = useState<string | null>(null);
  const [aiProvider, setAiProvider] = useState<'claude' | 'google_gpt'>('claude');
  const [showBulkProgress, setShowBulkProgress] = useState(false);
  const [isBulkAnalysisPolling] = useState(false);
  const [isStartingBackgroundProcessing, setIsStartingBackgroundProcessing] = useState(false);

  const mergeCachedResults = useCallback((results: Record<string, ClassificationResult>) => {
    setClassificationResults((prev) => {
      const updated = { ...prev };
      for (const [fileId, result] of Object.entries(results)) {
        if (!updated[fileId]) {
          updated[fileId] = result;
        }
      }
      return updated;
    });
  }, []);

  const handleClassifyFile = async (file: DropzoneFile, effectiveUnitId: string | null) => {
    if (classifyingFileIds.has(file.id)) return;

    setClassifyingFileIds((prev) => new Set(prev).add(file.id));
    setClassificationProgress((prev) => ({ ...prev, [file.id]: 0 }));

    progressIntervalsRef.current[file.id] = setInterval(() => {
      setClassificationProgress((prev) => {
        const current = prev[file.id] || 0;
        let next = current;
        if (current < 15) next = current + 1.5;
        else if (current < 40) next = current + 0.8;
        else if (current < 70) next = current + 0.5;
        else if (current < 95) next = current + 0.3;
        return { ...prev, [file.id]: next };
      });
    }, 500);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/building/${buildingId}/dropzone-classify`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fileId: file.id,
            fileName: file.name,
            mimeType: file.type,
            unitId: effectiveUnitId || undefined,
            userId: user?.id,
            aiProvider: aiProvider,
          }),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Fout bij classificeren (${response.status})`
        );
      }

      const result: ClassificationResult = await response.json();

      setClassificationResults((prev) => ({
        ...prev,
        [file.id]: result,
      }));

      if (result.suggestedLabels.length > 0) {
        const ddInfo = result.dueDiligenceAnswers?.extractedAnswers.length
          ? ` + ${result.dueDiligenceAnswers.extractedAnswers.length} DD vragen beantwoord`
          : '';
        const cacheInfo = result.fromCache ? ' (uit cache)' : '';
        toast.success(
          `AI heeft ${result.suggestedLabels.length} label(s) voorgesteld voor "${file.name}"${ddInfo}${cacheInfo}`
        );
      } else {
        toast.info(
          `Geen passend label gevonden voor "${file.name}". Kies handmatig een label.`
        );
      }
    } catch (err: any) {
      console.error("Error classifying file:", err);
      Sentry.captureException(err, {
        extra: { buildingId, fileId: file.id, context: "DropzoneTab classify" },
      });
      toast.error(err.message || "Fout bij classificeren van bestand");
    } finally {
      if (progressIntervalsRef.current[file.id]) {
        clearInterval(progressIntervalsRef.current[file.id]);
        delete progressIntervalsRef.current[file.id];
      }
      setClassificationProgress((prev) => ({ ...prev, [file.id]: 100 }));
      setTimeout(() => {
        setClassifyingFileIds((prev) => {
          const next = new Set(prev);
          next.delete(file.id);
          return next;
        });
        setClassificationProgress((prev) => {
          const next = { ...prev };
          delete next[file.id];
          return next;
        });
      }, 300);
    }
  };

  const handleReanalyzeFile = async (file: DropzoneFile, effectiveUnitId: string | null) => {
    if (reanalyzingFileId === file.id) return;

    setReanalyzingFileId(file.id);

    try {
      const deleteResponse = await fetchWithAuth(
        `${API_URL}/building/${buildingId}/dropzone-analysis/${file.id}`,
        { method: "DELETE" },
        session
      );

      if (!deleteResponse.ok) {
        const errorData = await deleteResponse.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Fout bij verwijderen cache (${deleteResponse.status})`
        );
      }

      // Clear existing classification result
      setClassificationResults((prev) => {
        const newResults = { ...prev };
        delete newResults[file.id];
        return newResults;
      });

      const classifyResponse = await fetchWithAuth(
        `${API_URL}/building/${buildingId}/dropzone-classify`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fileId: file.id,
            fileName: file.name,
            mimeType: file.type,
            unitId: effectiveUnitId || undefined,
            userId: user?.id,
            aiProvider: aiProvider,
          }),
        },
        session
      );

      if (!classifyResponse.ok) {
        const errorData = await classifyResponse.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Fout bij heranalyse (${classifyResponse.status})`
        );
      }

      const result: ClassificationResult = await classifyResponse.json();

      setClassificationResults((prev) => ({
        ...prev,
        [file.id]: result,
      }));

      toast.success(`Bestand "${file.name}" opnieuw geanalyseerd`);
    } catch (err: any) {
      console.error("Error re-analyzing file:", err);
      Sentry.captureException(err, {
        extra: { buildingId, fileId: file.id, context: "DropzoneTab reanalyze" },
      });
      toast.error(err.message || "Fout bij opnieuw analyseren van bestand");
    } finally {
      setReanalyzingFileId(null);
    }
  };

  const clearClassification = async (fileId: string) => {
    setClassificationResults((prev) => {
      const newResults = { ...prev };
      delete newResults[fileId];
      return newResults;
    });

    try {
      await fetchWithAuth(
        `${API_URL}/building/${buildingId}/dropzone-analysis/${fileId}`,
        { method: "DELETE" },
        session
      );
    } catch (err) {
      console.warn("[DropzoneTab] Failed to delete cached analysis from backend:", err);
    }
  };

  const handleStartBulkAnalysis = useCallback(async () => {
    const job = await startBulkAnalysisForBuilding(buildingId);
    if (job) {
      setShowBulkProgress(true);
    }
  }, [buildingId, startBulkAnalysisForBuilding]);

  const handleBulkAnalysisCancel = useCallback(async () => {
    if (currentBulkJob?.id) {
      await cancelBulkJob(currentBulkJob.id);
      setShowBulkProgress(false);
    }
  }, [currentBulkJob, cancelBulkJob]);

  const handleBulkAnalysisClose = useCallback(() => {
    setShowBulkProgress(false);
    onBulkAnalysisClose?.();
  }, [onBulkAnalysisClose]);

  const handleBulkAnalysisRefresh = useCallback(async () => {
    if (currentBulkJob?.id) {
      await getBulkJobStatus(currentBulkJob.id);
    }
  }, [currentBulkJob, getBulkJobStatus]);

  const handleStartBackgroundDDProcessing = useCallback(async () => {
    if (!session || !user?.id) {
      toast.error("Geen sessie beschikbaar");
      return;
    }

    setIsStartingBackgroundProcessing(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/building/${buildingId}/dropzone-process-all`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: user.id }),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Fout bij starten achtergrondverwerking");
      }

      const result = await response.json();
      toast.success(
        `DD-verwerking gestart: ${result.filesToProcess} van ${result.totalFiles} bestanden worden verwerkt (${result.alreadyProcessed} al eerder geanalyseerd). Je ontvangt een Slack-notificatie bij voortgang.`
      );
    } catch (err: any) {
      console.error("Error starting background DD processing:", err);
      Sentry.captureException(err);
      toast.error(err.message || "Kon achtergrondverwerking niet starten");
    } finally {
      setIsStartingBackgroundProcessing(false);
    }
  }, [buildingId, session, user?.id]);

  return {
    classifyingFileIds,
    classificationResults,
    setClassificationResults,
    classificationProgress,
    reanalyzingFileId,
    aiProvider,
    setAiProvider,
    showBulkProgress,
    setShowBulkProgress,
    isBulkAnalysisPolling,
    isStartingBackgroundProcessing,
    currentBulkJob,
    handleClassifyFile,
    handleReanalyzeFile,
    clearClassification,
    handleStartBulkAnalysis,
    handleBulkAnalysisCancel,
    handleBulkAnalysisClose,
    handleBulkAnalysisRefresh,
    handleStartBackgroundDDProcessing,
    mergeCachedResults,
  };
}
